import React, { useState, useEffect, useCallback } from 'react';
import {
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Chip,
  Box,
  TextField,
  InputAdornment,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Grid,
  Divider,
  Card,
  CardContent,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Pagination,
  Stack,
  Snackbar,
  Alert,
  FormControlLabel,
  Switch,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import {
  Add as AddIcon,
  AddShoppingCart as AddShoppingCartIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Warning as WarningIcon,
  Clear,
  History as HistoryIcon,
  FactCheck as FactCheckIcon
} from '@mui/icons-material';
import { api } from '../../utils/api';
import ProductForm from './ProductForm';
import ImageCarousel from '../shop/ImageCarousel';
import ProductMovementsDialog from '../warehouse/ProductMovementsDialog';
import { getProductImage } from '../../utils/productUtils';

const ProductList = ({ refreshKey = 0, onOpenManualLoad, onOpenInventory }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [openForm, setOpenForm] = useState(false);
  const [productToEdit, setProductToEdit] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [openDisableDialog, setOpenDisableDialog] = useState(false);
  const [productToDisable, setProductToDisable] = useState(null);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [openMovements, setOpenMovements] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [importingImageFromShop, setImportingImageFromShop] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [totalPages, setTotalPages] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  
  // Filter states
  const [filters, setFilters] = useState({
    code: '',
    name: '',
    category: '',
    brand: '',
    purchasePriceMin: '',
    purchasePriceMax: '',
    sellingPriceMin: '',
    sellingPriceMax: '',
    assemblyPriceMin: '',
    assemblyPriceMax: '',
    stockMin: '',
    stockMax: '',
    type: '', // 'Prodotto' o 'Servizio'
    inventoryStatus: '' // '' | 'confirmed' | 'pending'
  });

  // Column widths state - load from localStorage or use defaults
  const getDefaultColumnWidths = () => ({
    image: 80,
    code: 180,
    name: 200,
    category: 150,
    brand: 120,
    purchasePrice: 130,
    sellingPrice: 130,
    assemblyPrice: 150,
    stock: 150,
    type: 100,
    actions: 150
  });

  const [columnWidths, setColumnWidths] = useState(() => {
    const saved = localStorage.getItem('productList_columnWidths');
    return saved ? JSON.parse(saved) : getDefaultColumnWidths();
  });

  const [resizingColumn, setResizingColumn] = useState(null);
  const [resizeStartX, setResizeStartX] = useState(0);
  const [resizeStartWidth, setResizeStartWidth] = useState(0);

  // State for filter options (categories and brands)
  const [filterOptions, setFilterOptions] = useState({
    categories: [],
    brands: []
  });

  const loadFilterOptions = useCallback(async () => {
    try {
      const res = await api.get('/products/filter-options');
      if (res.data) {
        setFilterOptions({
          categories: res.data.categories || [],
          brands: res.data.brands || []
        });
      }
    } catch (err) {
      console.error('Error loading filter options:', err);
    }
  }, []);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        limit: itemsPerPage
      };
      
      if (searchTerm.trim()) {
        params.search = searchTerm.trim();
      }

      // Aggiungi tutti i filtri ai parametri della richiesta
      if (filters.code) {
        params.code = filters.code;
      }
      if (filters.name) {
        params.name = filters.name;
      }
      if (filters.category) {
        params.category = filters.category;
      }
      if (filters.brand) {
        params.brand = filters.brand;
      }
      if (filters.purchasePriceMin) {
        params.purchasePriceMin = filters.purchasePriceMin;
      }
      if (filters.purchasePriceMax) {
        params.purchasePriceMax = filters.purchasePriceMax;
      }
      if (filters.sellingPriceMin) {
        params.sellingPriceMin = filters.sellingPriceMin;
      }
      if (filters.sellingPriceMax) {
        params.sellingPriceMax = filters.sellingPriceMax;
      }
      if (filters.assemblyPriceMin) {
        params.assemblyPriceMin = filters.assemblyPriceMin;
      }
      if (filters.assemblyPriceMax) {
        params.assemblyPriceMax = filters.assemblyPriceMax;
      }
      if (filters.stockMin) {
        params.stockMin = filters.stockMin;
      }
      if (filters.stockMax) {
        params.stockMax = filters.stockMax;
      }
      if (filters.type) {
        params.type = filters.type;
      }
      if (filters.inventoryStatus === 'confirmed') {
        params.inventoryConfirmed = 'true';
      } else if (filters.inventoryStatus === 'pending') {
        params.inventoryConfirmed = 'false';
      }
      
      const res = await api.get('/products', { params });
      
      // Log per debug della risposta API
      console.log('Risposta API prodotti:', res.data);
      
      // Verifica se la risposta contiene un array products o se è direttamente un array
      if (res.data && res.data.products) {
        setProducts(res.data.products);
        setTotalPages(res.data.totalPages || 1);
        setTotalProducts(res.data.total || 0);
      } else if (Array.isArray(res.data)) {
        setProducts(res.data);
        setTotalPages(1);
        setTotalProducts(res.data.length);
      } else {
        console.error('Formato risposta API prodotti non valido:', res.data);
        setProducts([]);
        setTotalPages(1);
        setTotalProducts(0);
      }
      
      setError(null);
    } catch (err) {
      setError('Errore durante il caricamento dei prodotti');
      console.error('Error fetching products:', err);
      setProducts([]);
      setTotalPages(1);
      setTotalProducts(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, searchTerm, filters]);

  // Load filter options on mount
  useEffect(() => {
    loadFilterOptions();
  }, [loadFilterOptions]);

  // Fetch products when component mounts or pagination/filters change
  useEffect(() => {
    fetchProducts();
  }, [fetchProducts, refreshKey]);

  // Debounced search effect
  useEffect(() => {
    const timer = setTimeout(() => {
      if (currentPage !== 1) {
        setCurrentPage(1);
      } else {
        fetchProducts();
      }
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  // Debounced filter effect
  useEffect(() => {
    const timer = setTimeout(() => {
      if (currentPage !== 1) {
        setCurrentPage(1);
      } else {
        fetchProducts();
      }
    }, 300); // 300ms delay for filters

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  // Sottoscrizione agli eventi di aggiornamento
  useEffect(() => {
    // Funzione per gestire l'evento di aggiornamento della tariffa oraria
    const handleLaborRateUpdate = () => {
      console.log('Ricevuto evento di aggiornamento tariffa oraria');
      fetchProducts(); // Ricarica i prodotti per ottenere i prezzi aggiornati
    };

    // Aggiungi l'event listener
    window.addEventListener('laborRateUpdated', handleLaborRateUpdate);

    // Rimuovi l'event listener quando il componente viene smontato
    return () => {
      window.removeEventListener('laborRateUpdated', handleLaborRateUpdate);
    };
  }, [fetchProducts]);

  const handleOpenForm = (product = null) => {
    setProductToEdit(product);
    setOpenForm(true);
  };

  const handleCloseForm = () => {
    setOpenForm(false);
    setProductToEdit(null);
  };

  const handleProductSaved = () => {
    fetchProducts();
  };

  const handleOpenDeleteDialog = (product) => {
    setProductToDelete(product);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setProductToDelete(null);
  };

  const handleDeleteProduct = async () => {
    if (!productToDelete) return;
    
    try {
      await api.delete(`/products/${productToDelete.id}`);
      fetchProducts();
      handleCloseDeleteDialog();
      setSnackbar({
        open: true,
        message: 'Prodotto eliminato con successo',
        severity: 'success'
      });
    } catch (err) {
      // Se il prodotto è in uso, mostra il dialog per disabilitarlo
      if (err.response?.status === 409 && err.response?.data?.canDisable) {
        handleCloseDeleteDialog();
        setProductToDisable(productToDelete);
        setOpenDisableDialog(true);
      } else {
        setError('Errore durante l\'eliminazione del prodotto');
        console.error('Error deleting product:', err);
        setSnackbar({
          open: true,
          message: err.response?.data?.message || 'Errore durante l\'eliminazione del prodotto',
          severity: 'error'
        });
      }
    }
  };

  const handleCloseDisableDialog = () => {
    setOpenDisableDialog(false);
    setProductToDisable(null);
  };

  const handleDisableProduct = async () => {
    if (!productToDisable) return;
    
    try {
      await api.put(`/products/${productToDisable.id}/disable`);
      fetchProducts();
      handleCloseDisableDialog();
      setSnackbar({
        open: true,
        message: 'Prodotto disabilitato con successo. Non sarà più visibile nelle liste.',
        severity: 'success'
      });
    } catch (err) {
      setError('Errore durante la disabilitazione del prodotto');
      console.error('Error disabling product:', err);
      setSnackbar({
        open: true,
        message: err.response?.data?.message || 'Errore durante la disabilitazione del prodotto',
        severity: 'error'
      });
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    // Page reset is handled by the debounced effect
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const handleItemsPerPageChange = (event) => {
    setItemsPerPage(event.target.value);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  // Handle filter changes
  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
    setCurrentPage(1); // Reset to first page when filter changes
  };

  // Clear all filters
  const handleClearFilters = () => {
    setFilters({
      code: '',
      name: '',
      category: '',
      brand: '',
      purchasePriceMin: '',
      purchasePriceMax: '',
      sellingPriceMin: '',
      sellingPriceMax: '',
      assemblyPriceMin: '',
      assemblyPriceMax: '',
      stockMin: '',
      stockMax: '',
      type: ''
    });
    setCurrentPage(1);
  };

  // Handle column resize start
  const handleResizeStart = (columnKey, e) => {
    e.preventDefault();
    e.stopPropagation();
    setResizingColumn(columnKey);
    setResizeStartX(e.clientX);
    setResizeStartWidth(columnWidths[columnKey]);
  };

  // Handle column resize
  useEffect(() => {
    const handleResize = (e) => {
      if (resizingColumn) {
        const diff = e.clientX - resizeStartX;
        const newWidth = Math.max(50, resizeStartWidth + diff); // Minimum width 50px
        setColumnWidths(prev => ({
          ...prev,
          [resizingColumn]: newWidth
        }));
      }
    };

    const handleResizeEnd = () => {
      if (resizingColumn) {
        setColumnWidths(prev => {
          const updated = { ...prev };
          localStorage.setItem('productList_columnWidths', JSON.stringify(updated));
          return updated;
        });
        setResizingColumn(null);
      }
    };

    if (resizingColumn) {
      document.addEventListener('mousemove', handleResize);
      document.addEventListener('mouseup', handleResizeEnd);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleResize);
      document.removeEventListener('mouseup', handleResizeEnd);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [resizingColumn, resizeStartX, resizeStartWidth]);

  // I prodotti vengono già filtrati lato server, quindi li mostriamo direttamente
  const displayProducts = products;

  const handleOpenViewDialog = (product) => {
    setSelectedProduct(product);
    setOpenViewDialog(true);
  };

  const handleCloseViewDialog = () => {
    setOpenViewDialog(false);
    setSelectedProduct(null);
  };

  const handleImportImageFromShop = async () => {
    if (!selectedProduct?.id || !selectedProduct?.code) return;
    setImportingImageFromShop(true);
    try {
      let b2bProduct = null;
      try {
        const res = await api.get(`/b2b-products/code/${encodeURIComponent(selectedProduct.code)}`);
        b2bProduct = res.data;
      } catch (_) {
        const listRes = await api.get('/b2b-products', {
          params: { search: selectedProduct.code.trim(), limit: 5, page: 1 }
        });
        const list = listRes.data?.products || listRes.data || [];
        const arr = Array.isArray(list) ? list : [];
        const match = arr.find(p => (p.code || '').toString() === (selectedProduct.code || '').toString());
        if (match) b2bProduct = match;
        else if (arr.length > 0) b2bProduct = arr[0];
      }
      const imageUrl = (b2bProduct?.image || '').toString().trim();
      if (!imageUrl) {
        setSnackbar({ open: true, message: 'Nessuna immagine trovata nel listino shop per questo articolo', severity: 'info' });
        return;
      }
      const payload = {
        code: selectedProduct.code,
        name: selectedProduct.name,
        description: selectedProduct.description || '',
        category: selectedProduct.category || 'Generale',
        brand: selectedProduct.brand || '',
        price: selectedProduct.sellingPrice,
        purchasePrice: selectedProduct.purchasePrice,
        assemblyTime: selectedProduct.assemblyTime ?? 0,
        stock: selectedProduct.stock ?? 0,
        minStock: selectedProduct.minStock ?? 1,
        location: selectedProduct.location || '',
        image: imageUrl,
        isActive: selectedProduct.isActive !== false,
        isLabor: Boolean(selectedProduct.isLabor),
        doNotRestock: Boolean(selectedProduct.doNotRestock)
      };
      const res = await api.put(`/products/${selectedProduct.id}`, payload);
      setSelectedProduct(res.data);
      setProducts(prev => prev.map(p => p.id === res.data.id ? res.data : p));
      setSnackbar({ open: true, message: 'Immagine importata dal listino shop', severity: 'success' });
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.msg || 'Errore durante l\'importazione immagine';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setImportingImageFromShop(false);
    }
  };

  const hasNoProductImage = !selectedProduct?.image || String(selectedProduct.image).trim() === '';

  return (
    <>
      <Box sx={{ mb: 3 }}>
        {/* Top row with search and add button */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <TextField
            placeholder="Cerca prodotti..."
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={handleSearchChange}
            sx={{ width: '300px' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {onOpenInventory && (
            <Button
              variant="contained"
              color="info"
              startIcon={<FactCheckIcon />}
              onClick={onOpenInventory}
            >
              Inventario
            </Button>
          )}
          {onOpenManualLoad && (
            <Button
              variant="contained"
              color="success"
              startIcon={<AddShoppingCartIcon />}
              onClick={onOpenManualLoad}
            >
              Carico Manuale
            </Button>
          )}
          <Tooltip title="Aggiungi nuovo prodotto">
            <IconButton
              color="primary"
              onClick={() => handleOpenForm()}
              sx={{
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                '&:hover': { bgcolor: 'primary.dark' }
              }}
            >
              <AddIcon />
            </IconButton>
          </Tooltip>
          </Box>
        </Box>

        {/* Filtro inventario confermato */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <Typography variant="body2" color="textSecondary">
            Inventario:
          </Typography>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={filters.inventoryStatus}
            onChange={(_, value) => handleFilterChange('inventoryStatus', value || '')}
          >
            <ToggleButton value="">Tutti</ToggleButton>
            <ToggleButton value="confirmed">Confermati</ToggleButton>
            <ToggleButton value="pending">Da contare</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Info row with total products and items per page selector */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" color="textSecondary">
            Totale prodotti: {totalProducts} | Pagina {currentPage} di {totalPages}
          </Typography>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Prodotti per pagina</InputLabel>
            <Select
              value={itemsPerPage}
              label="Prodotti per pagina"
              onChange={handleItemsPerPageChange}
            >
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
              <MenuItem value={200}>200</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell 
                  sx={{ 
                    width: `${columnWidths.image}px`,
                    minWidth: `${columnWidths.image}px`,
                    maxWidth: `${columnWidths.image}px`,
                    position: 'relative',
                    paddingRight: '4px'
                  }}
                >
                  Immagine
                  <Box
                    onMouseDown={(e) => handleResizeStart('image', e)}
                    sx={{
                      position: 'absolute',
                      right: 0,
                      top: 0,
                      bottom: 0,
                      width: '4px',
                      cursor: 'col-resize',
                      backgroundColor: resizingColumn === 'image' ? 'primary.main' : 'transparent',
                      '&:hover': {
                        backgroundColor: 'action.hover'
                      }
                    }}
                  />
                </TableCell>
                <TableCell 
                  sx={{ 
                    width: `${columnWidths.code}px`,
                    minWidth: `${columnWidths.code}px`,
                    maxWidth: `${columnWidths.code}px`,
                    position: 'relative',
                    paddingRight: '4px'
                  }}
                >
                  Codice Prodotto
                  <Box
                    onMouseDown={(e) => handleResizeStart('code', e)}
                    sx={{
                      position: 'absolute',
                      right: 0,
                      top: 0,
                      bottom: 0,
                      width: '4px',
                      cursor: 'col-resize',
                      backgroundColor: resizingColumn === 'code' ? 'primary.main' : 'transparent',
                      '&:hover': {
                        backgroundColor: 'action.hover'
                      }
                    }}
                  />
                </TableCell>
                <TableCell 
                  sx={{ 
                    width: `${columnWidths.name}px`,
                    minWidth: `${columnWidths.name}px`,
                    maxWidth: `${columnWidths.name}px`,
                    position: 'relative',
                    paddingRight: '4px'
                  }}
                >
                  Nome Prodotto
                  <Box
                    onMouseDown={(e) => handleResizeStart('name', e)}
                    sx={{
                      position: 'absolute',
                      right: 0,
                      top: 0,
                      bottom: 0,
                      width: '4px',
                      cursor: 'col-resize',
                      backgroundColor: resizingColumn === 'name' ? 'primary.main' : 'transparent',
                      '&:hover': {
                        backgroundColor: 'action.hover'
                      }
                    }}
                  />
                </TableCell>
                <TableCell 
                  sx={{ 
                    width: `${columnWidths.category}px`,
                    minWidth: `${columnWidths.category}px`,
                    maxWidth: `${columnWidths.category}px`,
                    position: 'relative',
                    paddingRight: '4px'
                  }}
                >
                  Categoria Prodotto
                  <Box
                    onMouseDown={(e) => handleResizeStart('category', e)}
                    sx={{
                      position: 'absolute',
                      right: 0,
                      top: 0,
                      bottom: 0,
                      width: '4px',
                      cursor: 'col-resize',
                      backgroundColor: resizingColumn === 'category' ? 'primary.main' : 'transparent',
                      '&:hover': {
                        backgroundColor: 'action.hover'
                      }
                    }}
                  />
                </TableCell>
                <TableCell 
                  sx={{ 
                    width: `${columnWidths.brand}px`,
                    minWidth: `${columnWidths.brand}px`,
                    maxWidth: `${columnWidths.brand}px`,
                    position: 'relative',
                    paddingRight: '4px'
                  }}
                >
                  Marca Prodotto
                  <Box
                    onMouseDown={(e) => handleResizeStart('brand', e)}
                    sx={{
                      position: 'absolute',
                      right: 0,
                      top: 0,
                      bottom: 0,
                      width: '4px',
                      cursor: 'col-resize',
                      backgroundColor: resizingColumn === 'brand' ? 'primary.main' : 'transparent',
                      '&:hover': {
                        backgroundColor: 'action.hover'
                      }
                    }}
                  />
                </TableCell>
                <TableCell 
                  align="right"
                  sx={{ 
                    width: `${columnWidths.purchasePrice}px`,
                    minWidth: `${columnWidths.purchasePrice}px`,
                    maxWidth: `${columnWidths.purchasePrice}px`,
                    position: 'relative',
                    paddingRight: '4px'
                  }}
                >
                  Prezzo di Acquisto
                  <Box
                    onMouseDown={(e) => handleResizeStart('purchasePrice', e)}
                    sx={{
                      position: 'absolute',
                      right: 0,
                      top: 0,
                      bottom: 0,
                      width: '4px',
                      cursor: 'col-resize',
                      backgroundColor: resizingColumn === 'purchasePrice' ? 'primary.main' : 'transparent',
                      '&:hover': {
                        backgroundColor: 'action.hover'
                      }
                    }}
                  />
                </TableCell>
                <TableCell 
                  align="right"
                  sx={{ 
                    width: `${columnWidths.sellingPrice}px`,
                    minWidth: `${columnWidths.sellingPrice}px`,
                    maxWidth: `${columnWidths.sellingPrice}px`,
                    position: 'relative',
                    paddingRight: '4px'
                  }}
                >
                  Prezzo di Vendita
                  <Box
                    onMouseDown={(e) => handleResizeStart('sellingPrice', e)}
                    sx={{
                      position: 'absolute',
                      right: 0,
                      top: 0,
                      bottom: 0,
                      width: '4px',
                      cursor: 'col-resize',
                      backgroundColor: resizingColumn === 'sellingPrice' ? 'primary.main' : 'transparent',
                      '&:hover': {
                        backgroundColor: 'action.hover'
                      }
                    }}
                  />
                </TableCell>
                <TableCell 
                  align="right"
                  sx={{ 
                    width: `${columnWidths.assemblyPrice}px`,
                    minWidth: `${columnWidths.assemblyPrice}px`,
                    maxWidth: `${columnWidths.assemblyPrice}px`,
                    position: 'relative',
                    paddingRight: '4px'
                  }}
                >
                  Prezzo con Montaggio
                  <Box
                    onMouseDown={(e) => handleResizeStart('assemblyPrice', e)}
                    sx={{
                      position: 'absolute',
                      right: 0,
                      top: 0,
                      bottom: 0,
                      width: '4px',
                      cursor: 'col-resize',
                      backgroundColor: resizingColumn === 'assemblyPrice' ? 'primary.main' : 'transparent',
                      '&:hover': {
                        backgroundColor: 'action.hover'
                      }
                    }}
                  />
                </TableCell>
                <TableCell 
                  align="right"
                  sx={{ 
                    width: `${columnWidths.stock}px`,
                    minWidth: `${columnWidths.stock}px`,
                    maxWidth: `${columnWidths.stock}px`,
                    position: 'relative',
                    paddingRight: '4px'
                  }}
                >
                  Quantità in Magazzino
                  <Box
                    onMouseDown={(e) => handleResizeStart('stock', e)}
                    sx={{
                      position: 'absolute',
                      right: 0,
                      top: 0,
                      bottom: 0,
                      width: '4px',
                      cursor: 'col-resize',
                      backgroundColor: resizingColumn === 'stock' ? 'primary.main' : 'transparent',
                      '&:hover': {
                        backgroundColor: 'action.hover'
                      }
                    }}
                  />
                </TableCell>
                <TableCell 
                  sx={{ 
                    width: `${columnWidths.type}px`,
                    minWidth: `${columnWidths.type}px`,
                    maxWidth: `${columnWidths.type}px`,
                    position: 'relative',
                    paddingRight: '4px'
                  }}
                >
                  Tipologia
                  <Box
                    onMouseDown={(e) => handleResizeStart('type', e)}
                    sx={{
                      position: 'absolute',
                      right: 0,
                      top: 0,
                      bottom: 0,
                      width: '4px',
                      cursor: 'col-resize',
                      backgroundColor: resizingColumn === 'type' ? 'primary.main' : 'transparent',
                      '&:hover': {
                        backgroundColor: 'action.hover'
                      }
                    }}
                  />
                </TableCell>
                <TableCell 
                  align="center"
                  sx={{ 
                    width: `${columnWidths.actions}px`,
                    minWidth: `${columnWidths.actions}px`,
                    maxWidth: `${columnWidths.actions}px`
                  }}
                >
                  Azioni
                </TableCell>
              </TableRow>
              {/* Filter row */}
              <TableRow>
                <TableCell 
                  sx={{ 
                    width: `${columnWidths.image}px`,
                    minWidth: `${columnWidths.image}px`,
                    maxWidth: `${columnWidths.image}px`
                  }}
                >
                  <IconButton 
                    size="small" 
                    onClick={handleClearFilters}
                    title="Pulisci filtri"
                    disabled={Object.values(filters).every(f => !f)}
                  >
                    <Clear fontSize="small" />
                  </IconButton>
                </TableCell>
                <TableCell 
                  sx={{ 
                    width: `${columnWidths.code}px`,
                    minWidth: `${columnWidths.code}px`,
                    maxWidth: `${columnWidths.code}px`
                  }}
                >
                  <TextField
                    size="small"
                    placeholder="Filtra codice..."
                    value={filters.code}
                    onChange={(e) => handleFilterChange('code', e.target.value)}
                    sx={{ width: '100%' }}
                  />
                </TableCell>
                <TableCell 
                  sx={{ 
                    width: `${columnWidths.name}px`,
                    minWidth: `${columnWidths.name}px`,
                    maxWidth: `${columnWidths.name}px`
                  }}
                >
                  <TextField
                    size="small"
                    placeholder="Filtra nome..."
                    value={filters.name}
                    onChange={(e) => handleFilterChange('name', e.target.value)}
                    sx={{ width: '100%' }}
                  />
                </TableCell>
                <TableCell 
                  sx={{ 
                    width: `${columnWidths.category}px`,
                    minWidth: `${columnWidths.category}px`,
                    maxWidth: `${columnWidths.category}px`
                  }}
                >
                  <FormControl size="small" sx={{ width: '100%' }}>
                    <Select
                      value={filters.category}
                      onChange={(e) => handleFilterChange('category', e.target.value)}
                      displayEmpty
                    >
                      <MenuItem value="">Tutte</MenuItem>
                      {filterOptions.categories.map(cat => (
                        <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </TableCell>
                <TableCell 
                  sx={{ 
                    width: `${columnWidths.brand}px`,
                    minWidth: `${columnWidths.brand}px`,
                    maxWidth: `${columnWidths.brand}px`
                  }}
                >
                  <FormControl size="small" sx={{ width: '100%' }}>
                    <Select
                      value={filters.brand}
                      onChange={(e) => handleFilterChange('brand', e.target.value)}
                      displayEmpty
                    >
                      <MenuItem value="">Tutte</MenuItem>
                      {filterOptions.brands.map(brand => (
                        <MenuItem key={brand} value={brand}>{brand}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </TableCell>
                <TableCell 
                  align="right"
                  sx={{ 
                    width: `${columnWidths.purchasePrice}px`,
                    minWidth: `${columnWidths.purchasePrice}px`,
                    maxWidth: `${columnWidths.purchasePrice}px`
                  }}
                >
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <TextField
                      size="small"
                      type="number"
                      placeholder="Min"
                      value={filters.purchasePriceMin}
                      onChange={(e) => handleFilterChange('purchasePriceMin', e.target.value)}
                      sx={{ width: '70px' }}
                    />
                    <TextField
                      size="small"
                      type="number"
                      placeholder="Max"
                      value={filters.purchasePriceMax}
                      onChange={(e) => handleFilterChange('purchasePriceMax', e.target.value)}
                      sx={{ width: '70px' }}
                    />
                  </Box>
                </TableCell>
                <TableCell 
                  align="right"
                  sx={{ 
                    width: `${columnWidths.sellingPrice}px`,
                    minWidth: `${columnWidths.sellingPrice}px`,
                    maxWidth: `${columnWidths.sellingPrice}px`
                  }}
                >
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <TextField
                      size="small"
                      type="number"
                      placeholder="Min"
                      value={filters.sellingPriceMin}
                      onChange={(e) => handleFilterChange('sellingPriceMin', e.target.value)}
                      sx={{ width: '70px' }}
                    />
                    <TextField
                      size="small"
                      type="number"
                      placeholder="Max"
                      value={filters.sellingPriceMax}
                      onChange={(e) => handleFilterChange('sellingPriceMax', e.target.value)}
                      sx={{ width: '70px' }}
                    />
                  </Box>
                </TableCell>
                <TableCell 
                  align="right"
                  sx={{ 
                    width: `${columnWidths.assemblyPrice}px`,
                    minWidth: `${columnWidths.assemblyPrice}px`,
                    maxWidth: `${columnWidths.assemblyPrice}px`
                  }}
                >
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <TextField
                      size="small"
                      type="number"
                      placeholder="Min"
                      value={filters.assemblyPriceMin}
                      onChange={(e) => handleFilterChange('assemblyPriceMin', e.target.value)}
                      sx={{ width: '70px' }}
                    />
                    <TextField
                      size="small"
                      type="number"
                      placeholder="Max"
                      value={filters.assemblyPriceMax}
                      onChange={(e) => handleFilterChange('assemblyPriceMax', e.target.value)}
                      sx={{ width: '70px' }}
                    />
                  </Box>
                </TableCell>
                <TableCell 
                  align="right"
                  sx={{ 
                    width: `${columnWidths.stock}px`,
                    minWidth: `${columnWidths.stock}px`,
                    maxWidth: `${columnWidths.stock}px`
                  }}
                >
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <TextField
                      size="small"
                      type="number"
                      placeholder="Min"
                      value={filters.stockMin}
                      onChange={(e) => handleFilterChange('stockMin', e.target.value)}
                      sx={{ width: '70px' }}
                    />
                    <TextField
                      size="small"
                      type="number"
                      placeholder="Max"
                      value={filters.stockMax}
                      onChange={(e) => handleFilterChange('stockMax', e.target.value)}
                      sx={{ width: '70px' }}
                    />
                  </Box>
                </TableCell>
                <TableCell 
                  sx={{ 
                    width: `${columnWidths.type}px`,
                    minWidth: `${columnWidths.type}px`,
                    maxWidth: `${columnWidths.type}px`
                  }}
                >
                  <FormControl size="small" sx={{ width: '100%' }}>
                    <Select
                      value={filters.type}
                      onChange={(e) => handleFilterChange('type', e.target.value)}
                      displayEmpty
                    >
                      <MenuItem value="">Tutte</MenuItem>
                      <MenuItem value="Prodotto">Prodotto</MenuItem>
                      <MenuItem value="Servizio">Servizio</MenuItem>
                    </Select>
                  </FormControl>
                </TableCell>
                <TableCell 
                  sx={{ 
                    width: `${columnWidths.actions}px`,
                    minWidth: `${columnWidths.actions}px`,
                    maxWidth: `${columnWidths.actions}px`
                  }}
                >
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayProducts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} align="center">
                    {loading ? 'Caricamento...' : 'Nessun prodotto trovato'}
                  </TableCell>
                </TableRow>
              ) : (
                displayProducts.map((product) => (
                  <TableRow 
                    key={product.id}
                    hover
                    onClick={() => handleOpenViewDialog(product)}
                    sx={{ 
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: '#f5f5f5' }
                    }}
                  >
                    <TableCell 
                      sx={{ 
                        width: `${columnWidths.image}px`,
                        minWidth: `${columnWidths.image}px`,
                        maxWidth: `${columnWidths.image}px`
                      }}
                    >
                      <Box
                        component="img"
                        src={getProductImage(product.image)}
                        alt={product.name}
                        sx={{
                          width: 60,
                          height: 60,
                          objectFit: 'contain',
                          bgcolor: '#f5f5f5',
                          borderRadius: 1,
                          p: 0.5
                        }}
                        onError={(e) => {
                          e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjIwMCIgaGVpZ2h0PSIyMDAiIGZpbGw9IiNFRUVFRUUiLz48dGV4dCB4PSI1MCUiIHk9IjUwJSIgZG9taW5hbnQtYmFzZWxpbmU9Im1pZGRsZSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZmlsbD0iIzY2NiIgZm9udC1mYW1pbHk9IkFyaWFsIiBmb250LXNpemU9IjE0Ij5JbW1hZ2luZSBub24gZGlzcG9uaWJpbGU8L3RleHQ+PC9zdmc+';
                        }}
                      />
                    </TableCell>
                    <TableCell 
                      sx={{ 
                        width: `${columnWidths.code}px`,
                        minWidth: `${columnWidths.code}px`,
                        maxWidth: `${columnWidths.code}px`
                      }}
                    >
                      {product.code}
                    </TableCell>
                    <TableCell 
                      sx={{ 
                        width: `${columnWidths.name}px`,
                        minWidth: `${columnWidths.name}px`,
                        maxWidth: `${columnWidths.name}px`,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                      title={product.name}
                    >
                      {product.name}
                    </TableCell>
                    <TableCell 
                      sx={{ 
                        width: `${columnWidths.category}px`,
                        minWidth: `${columnWidths.category}px`,
                        maxWidth: `${columnWidths.category}px`,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                      title={product.category}
                    >
                      {product.category}
                    </TableCell>
                    <TableCell 
                      sx={{ 
                        width: `${columnWidths.brand}px`,
                        minWidth: `${columnWidths.brand}px`,
                        maxWidth: `${columnWidths.brand}px`,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                      title={product.brand || '-'}
                    >
                      {product.brand || '-'}
                    </TableCell>
                    <TableCell 
                      align="right"
                      sx={{ 
                        width: `${columnWidths.purchasePrice}px`,
                        minWidth: `${columnWidths.purchasePrice}px`,
                        maxWidth: `${columnWidths.purchasePrice}px`
                      }}
                    >
                      €{parseFloat(product.purchasePrice).toFixed(2)}
                    </TableCell>
                    <TableCell 
                      align="right"
                      sx={{ 
                        width: `${columnWidths.sellingPrice}px`,
                        minWidth: `${columnWidths.sellingPrice}px`,
                        maxWidth: `${columnWidths.sellingPrice}px`
                      }}
                    >
                      {product.isLabor ? (
                        <Chip label="N/A" size="small" variant="outlined" />
                      ) : (
                        `€${parseFloat(product.sellingPrice).toFixed(2)}`
                      )}
                    </TableCell>
                    <TableCell 
                      align="right"
                      sx={{ 
                        width: `${columnWidths.assemblyPrice}px`,
                        minWidth: `${columnWidths.assemblyPrice}px`,
                        maxWidth: `${columnWidths.assemblyPrice}px`
                      }}
                    >
                      {product.isLabor ? (
                        `€${parseFloat(product.sellingPriceWithAssembly || 0).toFixed(2)}`
                      ) : (
                        `€${parseFloat(product.sellingPriceWithAssembly || 0).toFixed(2)}`
                      )}
                    </TableCell>
                    <TableCell 
                      align="right"
                      sx={{ 
                        width: `${columnWidths.stock}px`,
                        minWidth: `${columnWidths.stock}px`,
                        maxWidth: `${columnWidths.stock}px`
                      }}
                    >
                      {product.isLabor ? (
                        <Chip label="∞" color="success" size="small" />
                      ) : product.stock < product.minStock ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, flexWrap: 'wrap' }}>
                          <Tooltip title="Scorta minima raggiunta">
                            <WarningIcon color="error" fontSize="small" />
                          </Tooltip>
                          <span>{product.stock}</span>
                          {product.inventoryConfirmed && (
                            <Chip label="✓" size="small" color="success" sx={{ height: 20, minWidth: 20 }} />
                          )}
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                          <span>{product.stock}</span>
                          {product.inventoryConfirmed && (
                            <Chip label="✓" size="small" color="success" sx={{ height: 20, minWidth: 20 }} />
                          )}
                        </Box>
                      )}
                    </TableCell>
                    <TableCell 
                      sx={{ 
                        width: `${columnWidths.type}px`,
                        minWidth: `${columnWidths.type}px`,
                        maxWidth: `${columnWidths.type}px`
                      }}
                    >
                      {product.isLabor ? (
                        <Chip label="Servizio" color="info" size="small" />
                      ) : (
                        <Chip label="Prodotto" color="default" size="small" />
                      )}
                    </TableCell>
                    <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                      <Tooltip title="Modifica">
                        <IconButton 
                          size="small" 
                          color="primary" 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenForm(product);
                          }}
                          aria-label="modifica"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Elimina">
                        <IconButton 
                          size="small" 
                          color="error" 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenDeleteDialog(product);
                          }}
                          aria-label="elimina"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Pagination Controls */}
      {!loading && totalPages > 1 && (
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
          <Stack spacing={2} alignItems="center">
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
              size="medium"
              showFirstButton
              showLastButton
            />
            <Typography variant="body2" color="textSecondary">
              Mostrando {Math.min((currentPage - 1) * itemsPerPage + 1, totalProducts)}-{Math.min(currentPage * itemsPerPage, totalProducts)} di {totalProducts} prodotti
            </Typography>
          </Stack>
        </Box>
      )}

      {/* Product Form Dialog */}
      <ProductForm
        open={openForm}
        handleClose={handleCloseForm}
        productToEdit={productToEdit}
        onProductSaved={handleProductSaved}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Conferma eliminazione</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Sei sicuro di voler eliminare il prodotto "{productToDelete?.name}"? Questa azione non può essere annullata.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} color="primary">
            Annulla
          </Button>
          <Button onClick={handleDeleteProduct} color="error" autoFocus>
            Elimina
          </Button>
        </DialogActions>
      </Dialog>

      {/* Disable Product Dialog */}
      <Dialog open={openDisableDialog} onClose={handleCloseDisableDialog}>
        <DialogTitle>Prodotto in uso</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Il prodotto "{productToDisable?.name}" non può essere eliminato perché è stato utilizzato in manutenzioni o vendite.
            <br /><br />
            {productToDisable && (productToDisable.stock ?? 0) > 0 ? (
              <>
                <strong>Non è possibile disabilitarlo perché ha giacenza maggiore di zero ({productToDisable.stock}).</strong>
                <br />
                Svuotare il magazzino (carico/scarico) prima di disabilitare. Se in seguito il prodotto viene riacquistato e la giacenza torna &gt; 0, verrà riabilitato automaticamente.
              </>
            ) : (
              <>
                Vuoi disabilitarlo? Il prodotto verrà nascosto dalle liste del magazzino, manutenzioni e vendite, ma rimarrà nel database per mantenere l'integrità dei dati storici.
                <br />
                Se in seguito viene riacquistato e la giacenza torna &gt; 0, verrà riabilitato automaticamente.
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDisableDialog} color="primary">
            Annulla
          </Button>
          <Button
            onClick={handleDisableProduct}
            color="warning"
            autoFocus
            disabled={productToDisable && (productToDisable.stock ?? 0) > 0}
          >
            Disabilita
          </Button>
        </DialogActions>
      </Dialog>

      {/* Product View Dialog */}
      <Dialog 
        open={openViewDialog} 
        onClose={handleCloseViewDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Dettagli Prodotto</Typography>
            {selectedProduct && (
              <Button
                onClick={() => setOpenMovements(true)}
                color="info"
                variant="outlined"
                startIcon={<HistoryIcon />}
              >
                Movimenti
              </Button>
            )}
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedProduct && (
            <Grid container spacing={3}>
              {/* Immagine del prodotto */}
              <Grid item xs={12}>
                <Card variant="outlined">
                  <Box sx={{ p: 2, position: 'relative' }}>
                    <ImageCarousel
                      imageUrls={selectedProduct.image || ''}
                      productName={selectedProduct.name}
                      height={200}
                      objectFit="contain"
                    />
                    {hasNoProductImage && !selectedProduct.isLabor && (
                      <Tooltip title="Cerca immagine nei listini shop e importala">
                        <IconButton
                          onClick={handleImportImageFromShop}
                          disabled={importingImageFromShop}
                          sx={{
                            position: 'absolute',
                            top: 16,
                            right: 16,
                            bgcolor: 'background.paper',
                            boxShadow: 1,
                            '&:hover': { bgcolor: 'action.hover' }
                          }}
                          size="medium"
                        >
                          {importingImageFromShop ? (
                            <CircularProgress size={24} />
                          ) : (
                            <SearchIcon />
                          )}
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Informazioni Generali</Typography>
                    <Divider sx={{ mb: 2 }} />
                    
                    <Typography variant="subtitle2" color="textSecondary">Codice Prodotto</Typography>
                    <Typography variant="body1" gutterBottom>{selectedProduct.code}</Typography>
                    
                    <Typography variant="subtitle2" color="textSecondary">Nome Prodotto</Typography>
                    <Typography variant="body1" gutterBottom>{selectedProduct.name}</Typography>
                    
                    <Typography variant="subtitle2" color="textSecondary">Descrizione</Typography>
                    <Typography variant="body1" gutterBottom>{selectedProduct.description || 'Nessuna descrizione'}</Typography>
                    
                    <Typography variant="subtitle2" color="textSecondary">Categoria</Typography>
                    <Typography variant="body1" gutterBottom>{selectedProduct.category}</Typography>
                    
                    <Typography variant="subtitle2" color="textSecondary">Marca</Typography>
                    <Typography variant="body1" gutterBottom>{selectedProduct.brand || '-'}</Typography>
                    
                    <Typography variant="subtitle2" color="textSecondary">Tipologia</Typography>
                    <Typography variant="body1" gutterBottom>
                      {selectedProduct.isLabor ? 'Servizio' : 'Prodotto'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Informazioni Economiche</Typography>
                    <Divider sx={{ mb: 2 }} />
                    
                    <Typography variant="subtitle2" color="textSecondary">Prezzo di Acquisto</Typography>
                    <Typography variant="body1" gutterBottom>€{parseFloat(selectedProduct.purchasePrice).toFixed(2)}</Typography>
                    
                    <Typography variant="subtitle2" color="textSecondary">Prezzo di Vendita</Typography>
                    <Typography variant="body1" gutterBottom>
                      {selectedProduct.isLabor ? 'N/A' : `€${parseFloat(selectedProduct.sellingPrice).toFixed(2)}`}
                    </Typography>
                    
                    <Typography variant="subtitle2" color="textSecondary">Tempo di Montaggio</Typography>
                    <Typography variant="body1" gutterBottom>{selectedProduct.assemblyTime || 0} minuti</Typography>
                    
                    <Typography variant="subtitle2" color="textSecondary">Prezzo con Montaggio</Typography>
                    <Typography variant="body1" gutterBottom>€{parseFloat(selectedProduct.sellingPriceWithAssembly || 0).toFixed(2)}</Typography>
                  </CardContent>
                </Card>
                
                <Card variant="outlined" sx={{ mt: 2 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Informazioni di Magazzino</Typography>
                    <Divider sx={{ mb: 2 }} />
                    
                    <Typography variant="subtitle2" color="textSecondary">Quantità in Magazzino</Typography>
                    <Typography variant="body1" gutterBottom>
                      {selectedProduct.isLabor ? '∞ (Servizio)' : selectedProduct.stock}
                    </Typography>
                    
                    <Typography variant="subtitle2" color="textSecondary">Scorta Minima</Typography>
                    <Typography variant="body1" gutterBottom>
                      {selectedProduct.isLabor ? 'N/A' : selectedProduct.minStock}
                    </Typography>
                    
                    <Typography variant="subtitle2" color="textSecondary">Posizione in Magazzino</Typography>
                    <Typography variant="body1" gutterBottom>
                      {selectedProduct.location || 'Non specificata'}
                    </Typography>

                    <Typography variant="subtitle2" color="textSecondary">Inventario fisico</Typography>
                    <Box sx={{ mb: 1 }}>
                      {selectedProduct.inventoryConfirmed ? (
                        <Chip
                          size="small"
                          color="success"
                          label={
                            selectedProduct.inventoryConfirmedAt
                              ? `Confermato il ${new Date(selectedProduct.inventoryConfirmedAt).toLocaleString('it-IT')}`
                              : 'Confermato'
                          }
                        />
                      ) : (
                        <Chip size="small" color="warning" variant="outlined" label="Da contare" />
                      )}
                    </Box>
                    
                    {!selectedProduct.isLabor && (
                      <Box sx={{ mt: 2 }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={Boolean(selectedProduct.doNotRestock)}
                              onChange={async (e) => {
                                try {
                                  const res = await api.put(`/products/${selectedProduct.id}`, {
                                    ...selectedProduct,
                                    doNotRestock: e.target.checked
                                  });
                                  setSelectedProduct(res.data);
                                  setProducts(prev => prev.map(p => p.id === res.data.id ? res.data : p));
                                  setSnackbar({
                                    open: true,
                                    message: res.data.doNotRestock ? 'Prodotto segnato come da non riassortire' : 'Flag da non riassortire rimosso',
                                    severity: 'success'
                                  });
                                } catch (err) {
                                  setSnackbar({
                                    open: true,
                                    message: 'Errore nell\'aggiornamento',
                                    severity: 'error'
                                  });
                                }
                              }}
                              color="primary"
                            />
                          }
                          label="Da non riassortire (acquisto una tantum / sostituito)"
                        />
                      </Box>
                    )}
                    
                    {selectedProduct.lastRestockDate && (
                      <>
                        <Typography variant="subtitle2" color="textSecondary">Ultimo Rifornimento</Typography>
                        <Typography variant="body1" gutterBottom>
                          {new Date(selectedProduct.lastRestockDate).toLocaleDateString('it-IT')}
                        </Typography>
                      </>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseViewDialog} color="primary">Chiudi</Button>
          <Button 
            onClick={() => {
              handleCloseViewDialog();
              handleOpenForm(selectedProduct);
            }} 
            color="primary"
          >
            Modifica
          </Button>
        </DialogActions>
      </Dialog>

      {/* Movements Dialog */}
      {selectedProduct && (
        <ProductMovementsDialog
          open={openMovements}
          onClose={() => setOpenMovements(false)}
          productId={selectedProduct.id}
        />
      )}

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ProductList;